import { useEffect, useMemo, useState } from 'react';
import { useStore } from '@/store/useStore';
import { ArrowLeft, ShieldAlert, Plus, Pencil, Trash2, Check, Loader2 } from 'lucide-react';
import {
  listAccessRoles, updateAccessRole, createAccessRole, deleteAccessRole,
  listWorkerTypes, upsertWorkerType, deleteWorkerType,
  type AccessRole, type WorkerType,
} from '@/auth/adminClient';
import type { RolePermissions } from '@/auth/permissions';

/**
 * Экран «Роли и права» (виден тем, у кого право «персонал»). Строки = роли,
 * столбцы = права (галочки). Источник истины — БД (таблица access_roles): меняем
 * галочку — сразу пишется на сервер и влияет на доступ всех с этой ролью.
 *
 * Модель: офисные роли (Руководитель/Управляющий/…) — каждая со своими правами.
 * Линейный персонал имеет ОБЩИЕ права (роль «Исполнитель» — одна строка). Сами
 * линейные должности (Ведущий/Диджей/…) — это типы работника (название+иконка),
 * управляются списком ниже; права у них общие (из строки «Исполнитель»).
 */

// Колонки прав. key совпадает с серверной моделью прав (RolePermissions).
const PAGE_KEYS = ['dashboard', 'calendar', 'sales', 'reports', 'personnel', 'workload', 'pay_rates'] as const;
// Колонка таблицы (события) → ключ в deals. «history» хранится как viewHistory.
const DEAL_COLS: { col: string; key: keyof RolePermissions['deals'] }[] = [
  { col: 'create', key: 'create' },
  { col: 'edit', key: 'edit' },
  { col: 'delete', key: 'delete' },
  { col: 'assignTeam', key: 'assignTeam' },
  { col: 'comment', key: 'comment' },
  { col: 'attach', key: 'attach' },
  { col: 'history', key: 'viewHistory' },
];

const GROUPS: { title: string; cols: { key: string; label: string }[] }[] = [
  {
    title: 'Разделы',
    cols: [
      { key: 'dashboard', label: 'Дашборд' },
      { key: 'calendar', label: 'Календарь' },
      { key: 'sales', label: 'Продажи' },
      { key: 'reports', label: 'Отчёты' },
      { key: 'personnel', label: 'Персонал' },
      // Узкое право: только вкладка «Загрузка» в «Персонале» (кассир сверяет отработанные
      // игры). У кого стоит «Персонал» — вкладка и так доступна, эту галочку ставить не надо.
      { key: 'workload', label: 'Загрузка' },
      // Справочник ставок расчёта зарплаты — только финансист/руководитель/супер-админ.
      { key: 'pay_rates', label: 'Ставки' },
    ],
  },
  {
    title: 'События',
    cols: [
      { key: 'create', label: 'Создавать' },
      { key: 'edit', label: 'Редактировать' },
      { key: 'delete', label: 'Удалять' },
      { key: 'assignTeam', label: 'Менять команду' },
      { key: 'comment', label: 'Комментарии' },
      { key: 'attach', label: 'Вложения' },
      { key: 'history', label: 'История' },
    ],
  },
  {
    title: 'Видимость',
    cols: [
      { key: 'allCities', label: 'Все города' },
      { key: 'citySwitcher', label: 'Выбор города' },
    ],
  },
];

// ── Маппинг прав БД ↔ плоский набор галочек ──────────────────
function permsToFlat(p: RolePermissions): Set<string> {
  const f = new Set<string>();
  for (const k of PAGE_KEYS) if (p.pages?.[k]) f.add(k);
  for (const { col, key } of DEAL_COLS) if (p.deals?.[key]) f.add(col);
  if (p.scope?.cities === 'all') f.add('allCities');
  if (p.citySwitcher) f.add('citySwitcher');
  return f;
}

// Собирает полный объект прав из галочек, СОХРАНЯЯ непоказанные поля (financeOnly,
// scope.games) из текущей роли — иначе сервер бы их затёр (PUT шлёт perms целиком).
function flatToPerms(flat: Set<string>, base: RolePermissions): RolePermissions {
  return {
    pages: {
      dashboard: flat.has('dashboard'),
      calendar: flat.has('calendar'),
      sales: flat.has('sales'),
      reports: flat.has('reports'),
      personnel: flat.has('personnel'),
      workload: flat.has('workload'),
      pay_rates: flat.has('pay_rates'),
    },
    deals: {
      create: flat.has('create'),
      edit: flat.has('edit'),
      delete: flat.has('delete'),
      assignTeam: flat.has('assignTeam'),
      comment: flat.has('comment'),
      attach: flat.has('attach'),
      viewHistory: flat.has('history'),
      financeOnly: base.deals?.financeOnly ?? false,
      okk: base.deals?.okk ?? false,
      okk_edit: base.deals?.okk_edit ?? false,
      declareGreen: base.deals?.declareGreen ?? false,
      confirmPurple: base.deals?.confirmPurple ?? false,
      editActualAmount: base.deals?.editActualAmount ?? false,
    },
    scope: {
      cities: flat.has('allCities') ? 'all' : 'own',
      games: base.scope?.games ?? 'own',
    },
    citySwitcher: flat.has('citySwitcher'),
  };
}

export function RolesMatrixPage() {
  const setPage = useStore((s) => s.setPage);
  const toast = useStore((s) => s.toast);
  const isSuper = useStore((s) => s.currentUser?.access_role === 'superadmin');

  const [roles, setRoles] = useState<AccessRole[]>([]);
  const [types, setTypes] = useState<WorkerType[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);

  const flatCols = useMemo(() => GROUPS.flatMap((g) => g.cols), []);

  useEffect(() => {
    if (!isSuper) return;
    (async () => {
      try {
        const [r, t] = await Promise.all([listAccessRoles(), listWorkerTypes()]);
        setRoles(r);
        setTypes(t);
      } catch (e) {
        toast({ kind: 'error', text: `Не удалось загрузить роли (${e instanceof Error ? e.message : e})` });
      } finally {
        setLoading(false);
      }
    })();
  }, [isSuper]); // eslint-disable-line

  if (!isSuper) {
    return (
      <div className="h-full grid place-items-center bg-surface-0 text-ink-2 p-6 text-center">
        <div className="space-y-3 max-w-sm">
          <div className="text-lg font-semibold text-ink-1">Доступ только у супер-админа</div>
          <p className="text-sm">Настройка ролей и прав доступна только под отдельным аккаунтом супер-админа.</p>
          <button className="btn-soft mx-auto" onClick={() => setPage('personnel')}>Назад</button>
        </div>
      </div>
    );
  }

  // Переключить право у роли: обновляем локально и пишем в БД (вся роль целиком).
  const toggle = async (role: AccessRole, col: string) => {
    const flat = permsToFlat(role.permissions);
    flat.has(col) ? flat.delete(col) : flat.add(col);
    const permissions = flatToPerms(flat, role.permissions);
    const next = { ...role, permissions };
    setRoles((cur) => cur.map((r) => (r.id === role.id ? next : r)));
    setSavingId(role.id);
    try {
      await updateAccessRole(role.id, { name: role.name, description: role.description, permissions });
    } catch (e) {
      toast({ kind: 'error', text: `Не сохранилось: ${e instanceof Error ? e.message : e}` });
      // откат
      setRoles((cur) => cur.map((r) => (r.id === role.id ? role : r)));
    } finally {
      setSavingId(null);
    }
  };

  // ── Свои роли доступа (не из 8 системных) ──
  // Решение заказчика 29.10.2026: для должностей вроде «Маркетолог»/«СММ», которым раньше
  // приходилось выдавать чужую роль («Менеджер ОП») за неимением своей, супер-админ заводит
  // здесь СВОЮ роль с нуля (права — пустые, дальше ставятся галочками в таблице выше) и
  // привязывает к ней слово(-а) из должности Битрикса — тогда такой человек получает роль
  // сам, при первом входе, без ручной правки в базе.
  const addRole = async () => {
    const name = window.prompt('Название новой роли (например, «Маркетолог»):', '')?.trim();
    if (!name) return;
    // Слово для авто-входа берём сразу из названия (без второго окна) — та же разбивка на
    // слова, что и в сопоставлении должности на сервере. Если название из нескольких слов
    // («Менеджер по рекламе») — ключевых слов будет несколько, сработает любое из них.
    // Поправить/убрать можно потом кликом по строке «слово: …» под названием роли.
    const keywords = name.toLowerCase().split(/[^a-zа-яё0-9]+/i).filter(Boolean);
    const id = `role_${name.toLowerCase().replace(/[^a-zа-я0-9]+/gi, '_')}_${Math.abs(hash(name + roles.length))}`;
    try {
      const r = await createAccessRole(id, name, keywords);
      setRoles((cur) => [...cur, r]);
      toast({ kind: 'success', text: `Роль «${name}» создана — отметьте права галочками в таблице выше` });
    } catch (e) {
      toast({ kind: 'error', text: `Не удалось создать (${e instanceof Error ? e.message : e})` });
    }
  };

  const editKeywords = async (role: AccessRole) => {
    const kwRaw = window.prompt(
      'Слово(-а) из должности в Битриксе (через запятую). Пусто — роль только вручную.',
      (role.bitrix_keywords ?? []).join(', '),
    );
    if (kwRaw == null) return; // отмена
    const keywords = kwRaw.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
    try {
      const r = await updateAccessRole(role.id, { name: role.name, description: role.description, permissions: role.permissions, bitrix_keywords: keywords });
      setRoles((cur) => cur.map((x) => (x.id === role.id ? r : x)));
      toast({ kind: 'success', text: 'Слова сохранены' });
    } catch (e) {
      toast({ kind: 'error', text: `Не удалось сохранить (${e instanceof Error ? e.message : e})` });
    }
  };

  const removeRole = async (role: AccessRole) => {
    if (!window.confirm(`Удалить роль «${role.name}»?`)) return;
    try {
      await deleteAccessRole(role.id);
      setRoles((cur) => cur.filter((x) => x.id !== role.id));
      toast({ kind: 'success', text: `Роль «${role.name}» удалена` });
    } catch (e) {
      // Сервер отвечает понятным текстом, если роль ещё кому-то назначена (409) — показываем как есть.
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Не удалось удалить' });
    }
  };

  // ── Линейные должности (worker_types) ──
  const addPosition = async () => {
    const name = window.prompt('Название новой должности:', '')?.trim();
    if (!name) return;
    const id = `wt_${name.toLowerCase().replace(/[^a-zа-я0-9]+/gi, '_')}_${Math.abs(hash(name + types.length))}`;
    try {
      const t = await upsertWorkerType(id, { name, icon: '🙂', sort_order: (types.length + 1) * 10 });
      setTypes((cur) => [...cur, t]);
      toast({ kind: 'success', text: `Должность «${name}» добавлена` });
    } catch (e) {
      toast({ kind: 'error', text: `Не удалось добавить (${e instanceof Error ? e.message : e})` });
    }
  };

  const renamePosition = async (t: WorkerType) => {
    const name = window.prompt('Новое название:', t.name)?.trim();
    if (!name || name === t.name) return;
    try {
      const upd = await upsertWorkerType(t.id, { name, icon: t.icon, sort_order: t.sort_order });
      setTypes((cur) => cur.map((x) => (x.id === t.id ? upd : x)));
    } catch (e) {
      toast({ kind: 'error', text: `Не удалось переименовать (${e instanceof Error ? e.message : e})` });
    }
  };

  const removePosition = async (t: WorkerType) => {
    if (t.is_system) { toast({ kind: 'warn', text: 'Системную должность удалить нельзя' }); return; }
    if (!window.confirm(`Удалить должность «${t.name}»?`)) return;
    try {
      await deleteWorkerType(t.id);
      setTypes((cur) => cur.filter((x) => x.id !== t.id));
      toast({ kind: 'success', text: `«${t.name}» удалена` });
    } catch (e) {
      toast({ kind: 'error', text: `Не удалось удалить (${e instanceof Error ? e.message : e})` });
    }
  };

  // Служебную роль супер-админа в таблицу не выводим: её права всегда полные и
  // редактированию не подлежат (иначе можно было урезать доступ самому себе).
  const officeRoles = roles
    .filter((r) => r.id !== 'performer' && r.id !== 'superadmin')
    .sort((a, b) => a.sort_order - b.sort_order);
  const performer = roles.find((r) => r.id === 'performer') ?? null;

  const renderRow = (role: AccessRole, label?: string) => {
    const flat = permsToFlat(role.permissions);
    // Своя (не системная) роль — показываем слово(-а) авто-входа и позволяем их поправить
    // или роль удалить. Системные (8 изначальных) этого не имеют — их заводит только код.
    const custom = !role.is_system;
    return (
      <tr key={role.id} className="border-b border-line/60 hover:bg-surface-1 group">
        <td className="sticky left-0 z-10 bg-surface-0 group-hover:bg-surface-1 px-3 py-2 border-r border-line">
          <div className="flex items-center gap-2 min-w-[200px]">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-ink-1 font-medium truncate">{label ?? role.name}</span>
                {savingId === role.id && <Loader2 className="w-3.5 h-3.5 animate-spin text-accent flex-shrink-0" />}
              </div>
              {custom && (
                <button type="button" onClick={() => void editKeywords(role)}
                  className="text-[11px] text-ink-3 hover:text-accent truncate block text-left">
                  {role.bitrix_keywords && role.bitrix_keywords.length > 0
                    ? `слово: ${role.bitrix_keywords.join(', ')}`
                    : 'слово для авто-входа не задано — нажмите'}
                </button>
              )}
            </div>
            {custom && (
              <button type="button" className="icon-btn !w-7 !h-7 opacity-0 group-hover:opacity-100 text-status-refused flex-shrink-0"
                title="Удалить роль" onClick={() => void removeRole(role)}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </td>
        {flatCols.map((c) => {
          const on = flat.has(c.key);
          return (
            <td key={c.key} className="px-2 py-2 text-center border-r border-line/40">
              <button
                onClick={() => toggle(role, c.key)}
                className={`w-5 h-5 rounded-md grid place-items-center mx-auto border transition-colors ${
                  on ? 'bg-accent border-accent text-white' : 'border-line bg-surface-1 hover:border-accent'
                }`}
                title={on ? 'Право есть — нажмите, чтобы снять' : 'Права нет — нажмите, чтобы выдать'}
              >
                {on && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
              </button>
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div className="h-full flex flex-col bg-surface-0">
      {/* Шапка */}
      <div className="h-12 px-4 flex items-center gap-3 border-b border-line bg-surface-1 flex-shrink-0">
        <button className="icon-btn" onClick={() => setPage('access')} title="Назад к доступам">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-base font-semibold flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-accent" /> Роли и права
        </h1>
        <div className="flex-1" />
        <button className="btn-primary !h-8 text-xs" onClick={addRole}>
          <Plus className="w-4 h-4" /> Добавить роль
        </button>
      </div>

      {/* Подсказка */}
      <div className="px-4 py-2 text-[12px] text-ink-3 border-b border-line bg-surface-0 flex-shrink-0">
        Галочка = право есть. Изменения сразу сохраняются в базу и влияют на всех с этой ролью.
        Линейный персонал имеет общие права (строка «Линейный персонал»).
        <div className="text-[11px] text-ink-3 mt-1">
          Служебная роль супер-администратора скрыта: её права всегда полные. У своих ролей (не из
          восьми системных) под названием — слово из должности Битрикса для авто-входа, нажмите, чтобы поправить.
        </div>
      </div>

      {loading ? (
        <div className="flex-1 grid place-items-center text-ink-3">
          <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Загрузка ролей…</span>
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <table className="text-sm border-collapse">
            <thead className="sticky top-0 z-20">
              <tr className="bg-surface-2">
                <th className="sticky left-0 z-30 bg-surface-2 px-3 py-2 text-left border-r border-b border-line text-[11px] uppercase tracking-wider text-ink-3">
                  Роль
                </th>
                {GROUPS.map((g) => (
                  <th key={g.title} colSpan={g.cols.length}
                    className="px-2 py-2 text-center border-r border-b border-line text-[11px] uppercase tracking-wider text-ink-2 font-bold">
                    {g.title}
                  </th>
                ))}
              </tr>
              <tr className="bg-surface-1">
                <th className="sticky left-0 z-30 bg-surface-1 border-r border-b border-line" />
                {flatCols.map((c) => (
                  <th key={c.key}
                    className="px-1 py-2 border-r border-b border-line/60 text-[11px] text-ink-2 font-medium whitespace-nowrap align-bottom"
                    style={{ minWidth: 78 }}>
                    <span className="block leading-tight">{c.label}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="bg-surface-2/60">
                <td colSpan={1 + flatCols.length} className="sticky left-0 px-3 py-1.5 text-[11px] uppercase tracking-wider text-ink-3 font-semibold">
                  Офисные роли
                </td>
              </tr>
              {officeRoles.map((r) => renderRow(r))}
              <tr className="bg-surface-2/60">
                <td colSpan={1 + flatCols.length} className="sticky left-0 px-3 py-1.5 text-[11px] uppercase tracking-wider text-ink-3 font-semibold">
                  Линейный персонал (общие права)
                </td>
              </tr>
              {performer && renderRow(performer, 'Линейный персонал')}
            </tbody>
          </table>

          {/* Линейные должности — справочник (название+иконка). Права у всех общие (см. строку выше). */}
          <div className="max-w-[760px] p-4">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-sm font-semibold text-ink-1">Линейные должности</h2>
              <span className="text-[11px] text-ink-3">— название и иконка; права общие из строки «Линейный персонал»</span>
              <div className="flex-1" />
              <button className="btn-primary !h-8 text-xs" onClick={addPosition}>
                <Plus className="w-4 h-4" /> Добавить должность
              </button>
            </div>
            <div className="panel divide-y divide-line/60">
              {types.map((t) => (
                <div key={t.id} className="flex items-center gap-3 px-3 py-2 group">
                  <span className="text-lg leading-none w-6 text-center">{t.icon ?? '🙂'}</span>
                  <span className="flex-1 text-ink-1">{t.name}</span>
                  <button className="icon-btn opacity-0 group-hover:opacity-100" title="Переименовать" onClick={() => renamePosition(t)}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    className={`icon-btn opacity-0 group-hover:opacity-100 ${t.is_system ? 'text-ink-3 cursor-not-allowed' : 'text-status-refused'}`}
                    title={t.is_system ? 'Системную должность удалить нельзя' : 'Удалить'} onClick={() => removePosition(t)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {types.length === 0 && <div className="px-3 py-4 text-sm text-ink-3">Должностей пока нет.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Детерминированный хэш строки → положительное число (для id новой должности).
function hash(s: string): number {
  let n = 0;
  for (let i = 0; i < s.length; i++) n = (n * 31 + s.charCodeAt(i)) >>> 0;
  return n;
}
